import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Percent, TrendingUp, Users } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";

export default function Commissions() {
  const { apiCall } = useAuth();
  const [data, setData] = useState({ summary: [], appointments: [] });
  const [loading, setLoading] = useState(true);

  const firstDay = new Date();
  firstDay.setDate(1);
  const lastDay = new Date(firstDay.getFullYear(), firstDay.getMonth() + 1, 0);

  const [startDate, setStartDate] = useState(firstDay.toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(lastDay.toISOString().slice(0, 10));

  const load = async () => {
    setLoading(true);
    try {
      const start = new Date(`${startDate}T00:00:00`).toISOString();
      const end = new Date(`${endDate}T23:59:59`).toISOString();
      const res = await apiCall("get", `/commissions?start_date=${encodeURIComponent(start)}&end_date=${encodeURIComponent(end)}`);
      setData(res.data);
    } catch {
      toast.error("Erro ao carregar comissões");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value || 0);

  const totals = data.summary.reduce(
    (acc, s) => ({
      revenue: acc.revenue + s.total_revenue,
      commission: acc.commission + s.total_commission,
      services: acc.services + s.total_services,
    }),
    { revenue: 0, commission: 0, services: 0 }
  );

  return (
    <div className="space-y-8" data-testid="commissions-page">
      <PageHeader overline="Equipe" title="Comissões" subtitle="Calculadas a partir de agendamentos concluídos no período" />

      <Card className="border border-border bg-card p-6 shadow-soft rounded-md">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md">
          <div>
            <Label className="text-xs uppercase tracking-widest">De</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="commission-start-date" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Até</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="commission-end-date" />
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border border-border bg-card p-6 shadow-soft rounded-md" data-testid="commission-total-services">
          <div className="flex items-start justify-between mb-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-heading">Total de Serviços</div>
            <Users className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          </div>
          <div className="text-2xl font-light tracking-tight font-heading">{totals.services}</div>
        </Card>
        <Card className="border border-border bg-card p-6 shadow-soft rounded-md" data-testid="commission-total-revenue">
          <div className="flex items-start justify-between mb-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-heading">Faturamento</div>
            <TrendingUp className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} style={{ color: "#166534" }} />
          </div>
          <div className="text-2xl font-light tracking-tight font-heading" style={{ color: "#166534" }}>
            {formatCurrency(totals.revenue)}
          </div>
        </Card>
        <Card className="border border-border bg-card p-6 shadow-soft rounded-md" data-testid="commission-total-commission">
          <div className="flex items-start justify-between mb-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-heading">Comissões a Pagar</div>
            <Percent className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} style={{ color: "#CBA871" }} />
          </div>
          <div className="text-2xl font-light tracking-tight font-heading" style={{ color: "#CBA871" }}>
            {formatCurrency(totals.commission)}
          </div>
        </Card>
      </div>

      <div>
        <h3 className="text-xl font-medium font-heading mb-4">Resumo por Funcionário</h3>
        <Card className="border border-border bg-card shadow-soft rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                <TableHead className="text-xs uppercase tracking-widest font-medium">Funcionário</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium">Serviços</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium">Faturamento</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium">Comissão</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium">Vales</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium">Faltas</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium">Bônus</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium">Líquido a pagar</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
              ) : data.summary.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">Nenhuma comissão no período. Conclua agendamentos com funcionários atribuídos.</TableCell></TableRow>
              ) : (
                data.summary.map((s) => (
                  <TableRow key={s.employee_id} data-testid={`commission-summary-${s.employee_id}`}>
                    <TableCell className="font-medium">{s.employee_name}</TableCell>
                    <TableCell>{s.total_services}</TableCell>
                    <TableCell>{formatCurrency(s.total_revenue)}</TableCell>
                    <TableCell className="font-medium" style={{ color: "#CBA871" }}>
                      {formatCurrency(s.total_commission)}
                    </TableCell>
                    <TableCell className="text-sm" style={{ color: "#F59E0B" }}>
                      {formatCurrency(s.vales_total || 0)}
                    </TableCell>
                    <TableCell className="text-sm" style={{ color: "#DC2626" }}>
                      {formatCurrency(s.faltas_total || 0)}
                    </TableCell>
                    <TableCell className="text-sm" style={{ color: "#7C3AED" }}>
                      {formatCurrency(s.bonus_total || 0)}
                    </TableCell>
                    <TableCell className="font-medium" style={{ color: (s.net_payable || 0) >= 0 ? "#166534" : "#991B1B" }}>
                      {formatCurrency(s.net_payable || 0)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      <div>
        <h3 className="text-xl font-medium font-heading mb-4">Detalhamento por Agendamento</h3>
        <Card className="border border-border bg-card shadow-soft rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                <TableHead className="text-xs uppercase tracking-widest font-medium">Data</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium">Cliente</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium">Funcionário</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium">Serviços</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium">Valor</TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-medium">Comissão</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.appointments.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">Sem agendamentos concluídos.</TableCell></TableRow>
              ) : (
                data.appointments.map((a) => (
                  <TableRow key={a.appointment_id}>
                    <TableCell className="text-sm">{new Date(a.date).toLocaleDateString("pt-BR")}</TableCell>
                    <TableCell>{a.client_name}</TableCell>
                    <TableCell>{a.employee_name}</TableCell>
                    <TableCell className="text-sm">{a.services.map((s) => s.service_name).join(", ")}</TableCell>
                    <TableCell>{formatCurrency(a.total_price)}</TableCell>
                    <TableCell className="font-medium" style={{ color: "#CBA871" }}>{formatCurrency(a.commission)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
